import { useEffect, useRef, useState } from 'react';

import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';

export const Accordion = ({
  header,
  children,
  defaultCollapsed = true,
  collapsed: controlledCollapsed,
  className,
  onToggle = () => {},
  chevronDataTestId,
  dataTestId,
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  // overflow visible is delayed after expand so Select dropdowns can escape the container
  const [overflowVisible, setOverflowVisible] = useState(!collapsed);
  const timerRef = useRef(null);

  // Sync overflowVisible when controlled collapsed changes
  useEffect(() => {
    if (isControlled) {
      setOverflowVisible(!controlledCollapsed);
    }
  }, [isControlled, controlledCollapsed]);

  // Clean up timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleToggle = () => {
    const next = !collapsed;
    if (!isControlled) setInternalCollapsed(next);
    onToggle?.(next);

    if (timerRef.current) clearTimeout(timerRef.current);

    if (next) {
      setOverflowVisible(false);
    } else {
      timerRef.current = setTimeout(() => setOverflowVisible(true), 310);
    }
  };

  return (
    <div
      className={cn(`flex min-w-0 flex-col ${collapsed ? 'gap-0' : 'gap-4'}`, className)}
      style={{ transition: 'gap 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}
      data-testid={dataTestId}
      data-collapsed={collapsed}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-3 w-full text-left group">
        <div className="flex-1 min-w-0">{header}</div>
        <div
          className="shrink-0 size-10 flex items-center justify-center"
          style={{
            transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms ease',
          }}>
          <HugeiconsIcon
            data-testid={chevronDataTestId}
            icon={ArrowDown01Icon}
            size={20}
            className="text-neutral-6"
          />
        </div>
      </button>

      <div
        className="grid min-h-0 min-w-0"
        style={{
          gridTemplateRows: collapsed ? '0fr' : '1fr',
          transition: 'grid-template-rows 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: overflowVisible ? 1 : 0,
        }}>
        <div
          className="min-h-0 min-w-0"
          style={{
            overflow: overflowVisible ? '' : 'hidden',
          }}>
          <div
            className="min-w-0"
            style={{
              opacity: collapsed ? 0 : 1,
              transform: collapsed ? 'translateY(-4px)' : 'translateY(0)',
              transition: 'opacity 250ms ease, transform 250ms ease',
              transitionDelay: collapsed ? '0ms' : '50ms',
            }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
